/**
 * GlassPanel — el contenedor del design system de Clobi.
 *
 * Portado de `clobi-design-system@0.1.0`. El proyecto de Claude Design solo
 * expone re-exports de una línea contra un bundle compilado, así que no hay
 * paquete que instalar: esta es la implementación del repo, escrita contra el
 * código del bundle (`_ds_bundle.js`, entregado sin minificar) y contra las
 * reglas de su `GlassPanel.prompt.md`.
 *
 * Diferencias deliberadas con el original, todas por el mismo motivo —el repo
 * es TypeScript y el sistema JavaScript—:
 *
 *   - `as` es un `ElementType` de verdad. El `.d.ts` del sistema lo declara
 *     como una unión de 20 tags seguida de un comentario que dice «+164 more», que
 *     es lo que produce un generador por inferencia y **no compila**.
 *   - `style` usa `React.CSSProperties`. El del sistema dice `CSSProperties`
 *     sin importarlo, así que tampoco compila.
 *
 * ─── Por qué existe ───────────────────────────────────────────────────────────
 *
 * Medido en este repo antes de escribirlo: el patrón de card está repetido a
 * mano **192 veces en 83 archivos** (`bg-surface` + `rounded-*` + `border`) y no
 * había ningún componente de panel. Cada pantalla elegía su radio, su borde y
 * su padding, y por eso dos cards vecinas no coinciden.
 *
 * ─── La regla que no hay que romper ───────────────────────────────────────────
 *
 * El sistema define CUATRO superficies y ninguna más. Si algo parece necesitar
 * una quinta, es un hueco de diseño y se resuelve en el sistema — no con un
 * `<div>` de fondo propio, que es exactamente lo que produjo las 192 copias.
 */
import { forwardRef } from 'react'
import type * as React from 'react'
import type { CSSProperties, ElementType, ReactNode } from 'react'

export type GlassLevel = 1 | 2 | 3 | 'solid'

export interface GlassPanelProps {
  /**
   * `1` para la card de pantalla · `2` para bloques destacados dentro de ella ·
   * `3` para controles.
   *
   * ⚠️ `'solid'` es **obligatorio** en cualquier capa que flote —menú, popover,
   * notificación, modal—: el vidrio detrás de texto chico, sobre contenido que
   * se mueve, no se lee.
   */
  level?: GlassLevel
  /** El tag que se renderiza. `'section'`, `'form'`, `'aside'`… */
  as?: ElementType
  /**
   * Aplica el padding estándar de card. Desactivar cuando el hijo es una tabla
   * que debe llegar al borde.
   */
  padded?: boolean
  title?: ReactNode
  subtitle?: ReactNode
  /** Botones o controles alineados a la derecha del encabezado. */
  actions?: ReactNode
  children?: ReactNode
  className?: string
  id?: string
  style?: CSSProperties
}

/**
 * Lo que el panel acepta además de sus props: atributos HTML —`role`, `onClick`,
 * los `aria-*`— y cualquier `data-*`, que es como `Modal` y `Drawer` le pasan su
 * estado al CSS. `title` se excluye porque acá es `ReactNode` y en HTML es
 * `string`.
 */
export type GlassPanelPropsConAtributos = GlassPanelProps &
  Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'style' | 'className'> & {
    [atributo: `data-${string}`]: unknown
  }

const CLASE_POR_NIVEL: Record<string, string> = {
  '1': 'cl-surface-1',
  '2': 'cl-surface-2',
  '3': 'cl-surface-3',
  solid: 'cl-surface-solid',
}

/**
 * El padding del cuerpo, en UNA sola declaración.
 *
 * El original del sistema combina `padding` con un `paddingTop` condicional, y
 * eso tiene un defecto: cuando el `paddingTop` queda en `undefined`, React lo
 * escribe como cadena vacía y **borra el lado superior que el shorthand acababa
 * de poner**. Verificado en el DOM: con `padded={false}` el elemento sale con
 * `padding-right/bottom/left: 0px` y sin `padding-top`. Hoy no se ve porque el
 * valor inicial de un div ya es 0, pero queda expuesto a que cualquier regla
 * externa le dé padding superior.
 */
function paddingDelCuerpo(padded: boolean, tieneEncabezado: boolean): string {
  if (!padded) return '0'
  // Con encabezado, la separación del título la define el cuerpo y es una sola
  // medida; sin encabezado, los cuatro lados llevan el padding de card.
  const arriba = tieneEncabezado ? '14px' : 'var(--pad-card)'
  return `${arriba} var(--pad-card) var(--pad-card)`
}

/**
 * ⚠️ Reenvía la ref al elemento raíz. `Modal` y `Drawer` la necesitan: la
 * trampa de foco busca los elementos enfocables DENTRO del panel, y sin ref no
 * tiene por dónde empezar.
 */
export const GlassPanel = forwardRef<HTMLElement, GlassPanelPropsConAtributos>(function GlassPanel({
  level = 1,
  as: Tag = 'div',
  padded = true,
  title,
  subtitle,
  actions,
  children,
  className = '',
  style,
  ...rest
}, ref) {
  const claseNivel = CLASE_POR_NIVEL[String(level)] ?? CLASE_POR_NIVEL['1']
  const tieneEncabezado = Boolean(title || subtitle || actions)

  return (
    <Tag ref={ref} className={`${claseNivel} ${className}`.trim()} style={style} {...rest}>
      {tieneEncabezado && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            // El encabezado lleva el padding lateral y superior de la card, pero
            // no el inferior: ese lo pone el cuerpo con su `paddingTop`, para que
            // la separación entre título y contenido sea una sola medida.
            padding: padded ? 'var(--pad-card) var(--pad-card) 0' : 0,
          }}
        >
          {/* `minWidth: 0` es lo que permite que un título largo se encoja en vez
              de empujar las acciones fuera del panel. Sin esto, un hijo flex se
              niega a bajar de su ancho de contenido. */}
          <div style={{ minWidth: 0 }}>
            {title && (
              <div
                style={{
                  fontSize: 'var(--type-card-title-size)',
                  fontWeight: 'var(--type-card-title-weight)' as CSSProperties['fontWeight'],
                }}
              >
                {title}
              </div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: 11.5,
                  color: 'rgb(var(--color-content-subtle))',
                  marginTop: 3,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {actions && (
            <div style={{ display: 'flex', gap: 6, flex: 'none' }}>{actions}</div>
          )}
        </div>
      )}
      {/* 🔴 Sin padding NO se interpone el `<div>`, y no es una optimización:
          un div de más ROMPE la cadena de flex cuando el panel es un
          contenedor flex. Es lo que le pasaba al `Modal` —que usa
          `padded={false}` y es `flex-direction: column` con `overflow:
          hidden`—: su `.cl-modal__body` dejaba de ser hijo directo, así que no
          se encogía, el `overflow-y: auto` no tenía a qué altura scrollear y
          **el formulario largo salía cortado sin barra**. Se vio en el modal
          de productos, no en un test.

          Un div con `padding: 0` y ningún otro estilo no aporta nada, así que
          quitarlo es idéntico salvo en eso. */}
      {paddingDelCuerpo(padded, tieneEncabezado) === '0' ? children : (
        <div style={{ padding: paddingDelCuerpo(padded, tieneEncabezado) }}>
          {children}
        </div>
      )}
    </Tag>
  )
})
